import { nullSafeGet, nullSafeGetOrElse } from './DataAccessUtils';
import { STOPLIGHT_COLORS } from './DataFormatterUtils';
import moment from 'moment-timezone';

export const assetInvoiceDetailsDisplayColor = (asset) => {
	const totalMaintenanceSpent = getAssetTotalExpenditure(asset);
	const originalCost = getAssetOriginalCost(asset);
	if (originalCost > 0) {
		const replacementThreshold = getAssetReplacementThreshold(asset);
		if (shouldAssetBeReplaced(asset)) {
			return STOPLIGHT_COLORS.pureRed;
		} else if (totalMaintenanceSpent / originalCost > replacementThreshold) {
			return STOPLIGHT_COLORS.pureYellow;
		} else {
			return STOPLIGHT_COLORS.pureGreen;
		}
	}

	return 'black';
};

export const getAssetCopy = (asset) => ({
	...asset,
	id: undefined,
	assetNumber: undefined,
	assetUUID: undefined,
	name: `Copy of ${asset.name}`,
	description: `Copy of ${asset.name} ${asset.description || ''}`,
	serialNumber: undefined,
});

export const ASSET_DEFAULT_REPLACEMENT_THRESHOLD = 0.8;
export const ASSET_DEFAULT_REPLACE_MARKUP_ON_ORIGINAL_COST = 0.1;

export const getAssetOriginalCost = (asset) =>
	nullSafeGetOrElse('capitalizedCost', asset, nullSafeGetOrElse('originalPrice', asset, 0));
export const getAssetReplacementThreshold = (asset) =>
	nullSafeGetOrElse('assetType.replacementThreshold', asset, ASSET_DEFAULT_REPLACEMENT_THRESHOLD);
export const getAssetReplacementMarkupOnOriginalCost = (asset) =>
	nullSafeGetOrElse(
		'assetType.replaceMarkupOnOriginalCost',
		asset,
		ASSET_DEFAULT_REPLACE_MARKUP_ON_ORIGINAL_COST
	);

export const getAssetTotalExpenditure = (asset) =>
	parseFloat(nullSafeGetOrElse('externalSpend', asset, 0)) +
	parseFloat(nullSafeGetOrElse('totalInvoiceSpendOnOpenWrench', asset, 0)) +
	parseFloat(nullSafeGetOrElse('totalInternalPartsSpend', asset, 0)) +
	parseFloat(nullSafeGetOrElse('totalInternalLaboursSpend', asset, 0));

export const shouldAssetBeReplaced = (asset, additional = 0) => {
	const totalMaintenanceSpent = getAssetTotalExpenditure(asset) + additional;
	const originalCost = getAssetOriginalCost(asset);
	const replacementThreshold = getAssetReplacementThreshold(asset);
	const replacementMarkupOnOriginalCost = getAssetReplacementMarkupOnOriginalCost(asset);
	return (
		totalMaintenanceSpent / (originalCost * (1 + replacementMarkupOnOriginalCost)) >
		replacementThreshold
	);
};

export const getBuyerSpecificAssetIdOrElseAssetId = (asset) =>
	nullSafeGetOrElse('buyerSpecificAssetId', asset, nullSafeGet('id', asset));

export const ASSET_FIELDS_DETAILS = [
	{ key: 'qrCodeImages', label: 'QR Code Images', type: 'images' },
	{ key: 'serialNumberImages', label: 'Serial No. Images', type: 'images' },
	{ key: 'assetImages', label: 'Asset Images', type: 'images' },
	// { key: 'name', label: 'Name' },
	// { key: 'isActive', label: 'Is Active' },
	// { key: 'description', label: 'Description' },
	{ key: 'manufacturer', label: 'Manufacturer', type: 'text' },
	{ key: 'modelNumber', label: 'Model Number', type: 'text' },
	{ key: 'serialNumber', label: 'Serial Number', type: 'textScanner' },
	{ key: 'assetNumber', label: 'Asset Number', type: 'text' },
	// { key: 'locationId', label: 'Location' },
	// { key: 'areaId', label: 'Area' },
	// { key: 'assetTypeId', label: 'Asset Type' },
	{ key: 'assetModelId', label: 'Asset Model', type: 'text' },
	// { key: 'images', label: 'Photos' },
	{ key: 'isLeased', label: 'Is Leased', type: 'switch' },
	{ key: 'installDate', label: 'Installation Date', type: 'date' },
	{ key: 'leaseStartDate', label: 'Lease Start Date', type: 'date' },
	{ key: 'leaseEndDate', label: 'Lease End Date', type: 'date' },
	{ key: 'purchaseDate', label: 'Purchase Date', type: 'date' },
	{ key: 'originalPrice', label: 'Purchase Price', type: 'currency' },
	{ key: 'capitalisedCost', label: 'Capitalised Cost', type: 'currency' },
	// { key: 'lifeExpectancyInMonths', label: 'Expected Useful Life (months)' },
	// { key: 'warranties', label: 'Has Warranty' },
	// { key: 'warrantyPeriod', label: 'Service Warranty Start and End Date' },
	// { key: 'supplierFacilityId', label: 'Warranty Service Provider' },
	// { key: 'NTEAmount', label: 'Service Warranty Not-To-Exceed Amount' },
	// { key: 'partWarranty', label: 'Has Part Warranty' },
	// { key: 'partWarrantyPeriod', label: 'Part Warranty Start and End Date' },
	// { key: 'vendorId', label: 'Part Warranty Vendor' },
];

export const ASSET_FIELDS_LABELS_MAP = {
	...Object.fromEntries(ASSET_FIELDS_DETAILS.map((field) => [field.key, field.label])),
	name: 'Asset Name',
	assetTypeId: 'Asset Type',
};

export const ADDITIONAL_FIELD_TYPE = 'additionalField';

/**
 * Given an array of full asset type objects, returns additional fields (requiredFields + optionalFields)
 * that are common across ALL asset types. Excludes fields that collide with standard ASSET_FIELDS_DETAILS keys.
 */
export const getCommonAdditionalFields = (assetTypes) => {
	if (!assetTypes || assetTypes.length === 0) return [];

	const standardKeys = new Set(ASSET_FIELDS_DETAILS.map((f) => f.key));

	// For each asset type, collect the set of additional field keys
	const keySetsPerType = assetTypes.map((at) => {
		const required = Array.isArray(at.requiredFields) ? at.requiredFields : [];
		const optional = Array.isArray(at.optionalFields) ? at.optionalFields : [];
		return new Set([...required, ...optional].map((f) => f.key).filter((k) => k));
	});

	// Intersect all key sets
	const commonKeys = new Set(keySetsPerType[0]);
	for (let i = 1; i < keySetsPerType.length; i++) {
		commonKeys.forEach((key) => {
			if (!keySetsPerType[i].has(key)) {
				commonKeys.delete(key);
			}
		});
	}

	// Build deduplicated field objects from the first type, excluding standard field keys
	const seen = new Set();
	const firstTypeFields = [
		...(Array.isArray(assetTypes[0].requiredFields) ? assetTypes[0].requiredFields : []),
		...(Array.isArray(assetTypes[0].optionalFields) ? assetTypes[0].optionalFields : []),
	];
	return firstTypeFields.filter((f) => {
		if (!commonKeys.has(f.key) || standardKeys.has(f.key) || seen.has(f.key)) return false;
		seen.add(f.key);
		return true;
	});
};

export const getScheduledTransferInitialValues = (asset) => {
	const scheduledTransfer = nullSafeGet('nextScheduledTransfer', asset);
	if (!scheduledTransfer) return undefined;
	const isLocationTransfer = !!nullSafeGet('nextScheduledTransfer.state.locationId', asset);
	const tz = nullSafeGetOrElse(
		'nextScheduledTransfer.state.location.timeZoneId',
		asset,
		nullSafeGetOrElse(
			'nextScheduledTransfer.state.stockLocation.timeZoneId',
			asset,
			moment.tz.guess()
		)
	);
	const triggerAt = scheduledTransfer.triggerAt
		? moment.tz(scheduledTransfer.triggerAt, tz)
		: moment.tz(moment(), tz);
	return isLocationTransfer
		? {
				locationId: nullSafeGet('nextScheduledTransfer.state.locationId', asset),
				areaId: nullSafeGet('nextScheduledTransfer.state.areaId', asset),
				transferNote: nullSafeGet('nextScheduledTransfer.transferNote', asset),
				triggerAt,
				transferTimezone: tz,
		  }
		: {
				stockLocationId: nullSafeGet('nextScheduledTransfer.state.stockLocationId', asset),
				equipmentTypeId: nullSafeGet('nextScheduledTransfer.state.equipmentTypeId', asset),
				transferNote: nullSafeGet('nextScheduledTransfer.transferNote', asset),
				triggerAt,
				transferTimezone: tz,
		  };
};
